import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { PAYMENT_METHOD_LABELS, formatCents, type PaymentMethod } from '@amai/contracts';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Field } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { errorMessage } from '@/lib/errors';
import { T } from '@/design/tokens';
import { useBankAccounts, todayISO } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

/** Faixa vermelha com a mensagem que a API devolveu, já traduzida. */
function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
      <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{errorMessage(error)}</span>
    </div>
  );
}

/**
 * Baixa de pagamento.
 *
 * O valor vem preenchido com o saldo em aberto — o caso comum é pagar tudo.
 * Pagamento parcial é digitar outro valor, não escolher um modo diferente.
 */
export function PayDialog({
  open,
  openCents,
  businessId,
  businessName,
  loading,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  openCents: number;
  /** Empresa do lançamento: a baixa só sai de um banco ou caixa dela. */
  businessId: string | null;
  businessName: string | null;
  loading: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (body: {
    bankAccountId: string;
    paidAt: string;
    amountCents: number;
    method: PaymentMethod;
    notes?: string | null;
  }) => void;
}) {
  const accounts = useBankAccounts();
  const [bankAccountId, setBankAccountId] = useState('');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [paidAt, setPaidAt] = useState(todayISO());
  const [method, setMethod] = useState<PaymentMethod>('PIX');
  const [notes, setNotes] = useState('');
  const [lastOpen, setLastOpen] = useState(open);

  // Abrir de novo recomeça com o saldo em aberto preenchido.
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setAmountCents(openCents);
      setPaidAt(todayISO());
      setNotes('');
      setBankAccountId('');
    }
  }

  // Só os bancos da empresa do lançamento: a API recusaria os outros, e
  // oferecer um que vai ser recusado é pegadinha.
  const contasDaEmpresa = (accounts.data ?? []).filter(
    (account) => !businessId || account.businessId === businessId,
  );

  const value = amountCents ?? 0;
  const partial = value > 0 && value < openCents;
  const invalid = value <= 0 || value > openCents || !bankAccountId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dar baixa na parcela"
      description={`Saldo em aberto: ${formatCents(openCents)}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={invalid}
            loading={loading}
            onClick={() =>
              onConfirm({
                bankAccountId,
                paidAt: new Date(`${paidAt}T12:00:00.000Z`).toISOString(),
                amountCents: value,
                method,
                notes: notes.trim() || null,
              })
            }
          >
            Confirmar baixa
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} />

      <Field
        label="Conta ou caixa"
        htmlFor="pay-account"
        hint={
          businessName
            ? contasDaEmpresa.length > 0
              ? `Só aparecem os bancos e caixas de ${businessName}`
              : `${businessName} ainda não tem banco ou caixa. Cadastre em Financeiro → Contas e caixas.`
            : undefined
        }
      >
        <select
          id="pay-account"
          className="input"
          value={bankAccountId}
          onChange={(event) => setBankAccountId(event.target.value)}
        >
          <option value="">Selecione…</option>
          {contasDaEmpresa.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name} · saldo {formatCents(account.currentBalanceCents)}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid-2">
        <Field label="Valor pago" htmlFor="pay-amount">
          <MoneyInput
            id="pay-amount"
            value={amountCents}
            onChange={setAmountCents}
            invalid={value > openCents}
          />
        </Field>

        <Field label="Data do pagamento" htmlFor="pay-date">
          <DateInput
            id="pay-date"
            className="input"
            value={paidAt}
            onChange={(iso) => setPaidAt(iso)}
          />
        </Field>
      </div>

      {value > openCents && (
        <p className="field__error" role="alert">
          O valor é maior que o saldo em aberto desta parcela.
        </p>
      )}
      {partial && (
        <p className="field__hint" style={{ color: T.amber }}>
          Pagamento parcial: ficará em aberto {formatCents(openCents - value)}.
        </p>
      )}

      <Field label="Forma de pagamento" htmlFor="pay-method">
        <select
          id="pay-method"
          className="input"
          value={method}
          onChange={(event) => setMethod(event.target.value as PaymentMethod)}
        >
          {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Observação (opcional)" htmlFor="pay-notes">
        <input
          id="pay-notes"
          className="input"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Comprovante, número da transferência…"
        />
      </Field>
    </Modal>
  );
}

/**
 * Estorno.
 *
 * A tela diz em texto o que vai acontecer: a baixa NÃO some, entra um
 * contra-lançamento ao lado dela. É a diferença que o operador precisa
 * entender para confiar no extrato depois.
 */
export function ReverseDialog({
  open,
  amountCents,
  loading,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  amountCents: number;
  loading: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [lastOpen, setLastOpen] = useState(open);

  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setReason('');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Estornar baixa"
      description={`Contra-lançamento de ${formatCents(-amountCents)}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={reason.trim().length < 5}
            loading={loading}
            onClick={() => onConfirm(reason.trim())}
          >
            Confirmar estorno
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} />

      <p style={{ fontSize: 13.5, color: T.inkSoft, lineHeight: 1.6, marginTop: 0 }}>
        A baixa original <strong>não será apagada</strong>. Entra um contra-lançamento negativo ao
        lado dela, a parcela volta para em aberto e o valor retorna ao saldo da conta. As duas
        linhas continuam visíveis no extrato.
      </p>

      <Field label="Motivo do estorno (vai para a auditoria)" htmlFor="reverse-reason">
        <textarea
          id="reverse-reason"
          className="input"
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Ex.: pagamento feito em duplicidade; valor creditado de volta pelo fornecedor"
        />
      </Field>
    </Modal>
  );
}

export function CancelEntryDialog({
  open,
  loading,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [lastOpen, setLastOpen] = useState(open);

  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setReason('');
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancelar lançamento"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Voltar
          </Button>
          <Button
            variant="danger"
            disabled={reason.trim().length < 5}
            loading={loading}
            onClick={() => onConfirm(reason.trim())}
          >
            Cancelar lançamento
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} />

      <p style={{ fontSize: 13.5, color: T.inkSoft, lineHeight: 1.6, marginTop: 0 }}>
        O lançamento <strong>não é apagado</strong>: fica registrado como cancelado, com o motivo e
        o seu nome. Se já houve baixa, estorne o pagamento antes.
      </p>

      <Field label="Motivo do cancelamento" htmlFor="cancel-reason">
        <textarea
          id="cancel-reason"
          className="input"
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Ex.: compra cancelada com o fornecedor"
        />
      </Field>
    </Modal>
  );
}
